import React from 'react';
import { View, StyleSheet, ViewStyle, StyleProp } from 'react-native';

interface PixelContainerProps {
  children: React.ReactNode;
  backgroundColor: string;
  cornerSize?: number;
  style?: StyleProp<ViewStyle>;
}

const PixelContainer = React.memo(function PixelContainer({
  children,
  backgroundColor,
  cornerSize = 4,
  style,
}: PixelContainerProps) {
  // Flatten style to extract flex properties and padding
  const flatStyle = StyleSheet.flatten(style) || {};
  const flexProps: ViewStyle = {};
  const paddingProps: ViewStyle = {};

  // Extract flex-related properties to pass to content
  if (flatStyle.flexDirection) flexProps.flexDirection = flatStyle.flexDirection;
  if (flatStyle.alignItems) flexProps.alignItems = flatStyle.alignItems;
  if (flatStyle.justifyContent) flexProps.justifyContent = flatStyle.justifyContent;
  if (flatStyle.gap !== undefined) flexProps.gap = flatStyle.gap;

  // Extract padding from style to apply to middle section
  const hasPadding = flatStyle.padding !== undefined ||
    flatStyle.paddingHorizontal !== undefined ||
    flatStyle.paddingVertical !== undefined ||
    flatStyle.paddingLeft !== undefined ||
    flatStyle.paddingRight !== undefined ||
    flatStyle.paddingTop !== undefined ||
    flatStyle.paddingBottom !== undefined;

  if (flatStyle.padding !== undefined) {
    paddingProps.padding = flatStyle.padding;
  } else {
    if (flatStyle.paddingHorizontal !== undefined) {
      paddingProps.paddingHorizontal = flatStyle.paddingHorizontal;
    }
    if (flatStyle.paddingVertical !== undefined) {
      paddingProps.paddingVertical = flatStyle.paddingVertical;
    }
    if (flatStyle.paddingLeft !== undefined) paddingProps.paddingLeft = flatStyle.paddingLeft;
    if (flatStyle.paddingRight !== undefined) paddingProps.paddingRight = flatStyle.paddingRight;
    if (flatStyle.paddingTop !== undefined) paddingProps.paddingTop = flatStyle.paddingTop;
    if (flatStyle.paddingBottom !== undefined) paddingProps.paddingBottom = flatStyle.paddingBottom;
  }

  // Remove padding from the wrapper style since we'll apply it to the middle section
  const wrapperStyle: ViewStyle = { ...flatStyle };
  delete wrapperStyle.padding;
  delete wrapperStyle.paddingHorizontal;
  delete wrapperStyle.paddingVertical;
  delete wrapperStyle.paddingLeft;
  delete wrapperStyle.paddingRight;
  delete wrapperStyle.paddingTop;
  delete wrapperStyle.paddingBottom;

  // Build middle section style
  const middleStyle: ViewStyle = [
    { backgroundColor },
    hasPadding ? paddingProps : { paddingHorizontal: cornerSize },
    flexProps
  ];

  return (
    <View style={[styles.wrapper, wrapperStyle]}>
      {/* Top Cap */}
      <View style={{ 
        height: cornerSize, 
        backgroundColor, 
        marginHorizontal: cornerSize 
      }} />
      {/* Middle Body (Content) */}
      <View style={middleStyle}>
        {children}
      </View>
      {/* Bottom Cap */}
      <View style={{ 
        height: cornerSize, 
        backgroundColor, 
        marginHorizontal: cornerSize 
      }} />
    </View>
  );
});

export default PixelContainer;

const styles = StyleSheet.create({
  wrapper: {
    // Ensures the caps stretch to match the body width
    alignItems: 'stretch', 
    flexDirection: 'column',
  },
});
